import { css, html, LitElement } from 'lit'
import { property } from 'lit/decorators.js'
import { createRef, type Ref, ref } from 'lit/directives/ref.js'
import { safeCustomElement } from '@/utilities/custom-element'
import type { BreadcrumbType } from './types'

@safeCustomElement('mdc-breadcrumb')
export class MdcBreadcrumb extends LitElement {
  @property({ type: String }) type: BreadcrumbType = 'plain-text'
  @property({ type: String, attribute: 'avatar-src' }) avatarSrc = ''
  @property({ type: String, attribute: 'avatar-alt' }) avatarAlt = ''
  @property({ type: String, attribute: 'full-name' }) fullName = ''
  @property({ type: String }) current = ''

  private _linkWrapperRef: Ref<HTMLDivElement> = createRef()

  static styles = css`
		:host {
			display: block;
			font-family: var(--typography-font-family-sans);
			/* CSS Custom Properties for customization */
			--mdc-breadcrumb-gap: var(--spacing-050);
			--mdc-breadcrumb-font-family-current: var(--typography-font-family-sans);
			--mdc-breadcrumb-font-family-name: var(--typography-font-family-sans);
			--mdc-breadcrumb-font-size: var(--text-075);
			--mdc-breadcrumb-font-size-lg: var(--text-087-and-half);
			--mdc-breadcrumb-font-weight-current: 450;
			--mdc-breadcrumb-font-weight-name: 600;
			--mdc-breadcrumb-line-height: 1;
			--mdc-breadcrumb-color-current: var(--color-functional-gray-black);
			--mdc-breadcrumb-color-name: var(--color-functional-gray-black);
			--mdc-breadcrumb-avatar-size: 1.5rem;
			--mdc-breadcrumb-avatar-size-lg: 2rem;
			--mdc-breadcrumb-avatar-border-color: var(--color-functional-gray-75);
			--mdc-breadcrumb-avatar-border-radius: var(--radius-full);
			--mdc-breadcrumb-divider-height: 1em;
			--mdc-breadcrumb-divider-height-lg: var(--text-087-and-half);
		}

		.mdc-breadcrumb,
		.mdc-breadcrumb *,
		.mdc-breadcrumb *::before,
		.mdc-breadcrumb *::after {
			box-sizing: border-box;
		}

		.mdc-breadcrumb ul {
			list-style: none;
			padding: 0;
			margin: 0;
		}

		.mdc-breadcrumb li {
			display: inline;
			margin: 0;
			padding: 0;
		}

		.mdc-breadcrumb a {
			text-decoration: none;
			color: inherit;
		}

		.mdc-breadcrumb {
			display: flex;
			align-items: center;
			gap: var(--mdc-breadcrumb-gap);
		}

		.mdc-breadcrumb ul {
			display: flex;
			align-items: center;
			gap: var(--mdc-breadcrumb-gap);
		}

		.mdc-breadcrumb__item {
			display: flex;
			align-items: center;
		}

		.mdc-breadcrumb__item--current {
			font-family: var(--mdc-breadcrumb-font-family-current);
			color: var(--mdc-breadcrumb-color-current);
			font-weight: var(--mdc-breadcrumb-font-weight-current);
			line-height: var(--mdc-breadcrumb-line-height);
			font-size: var(--mdc-breadcrumb-font-size);
		}

		.mdc-breadcrumb__item--separator {
			height: var(--mdc-breadcrumb-divider-height);
		}

		.mdc-breadcrumb__name {
			font-family: var(--mdc-breadcrumb-font-family-name);
			color: var(--mdc-breadcrumb-color-name);
			font-weight: var(--mdc-breadcrumb-font-weight-name);
			line-height: var(--mdc-breadcrumb-line-height);
			font-size: var(--mdc-breadcrumb-font-size);
		}

		.mdc-breadcrumb__item--link a {
			line-height: 1;
		}

		.mdc-breadcrumb__item--current,
		.mdc-breadcrumb__item--link a,
		.mdc-breadcrumb__name {
			font-size: var(--mdc-breadcrumb-font-size);
		}

		.mdc-breadcrumb__avatar {
			width: var(--mdc-breadcrumb-avatar-size);
			height: var(--mdc-breadcrumb-avatar-size);
			border: 1px solid var(--mdc-breadcrumb-avatar-border-color);
			border-radius: var(--mdc-breadcrumb-avatar-border-radius);
		}

		.mdc-breadcrumb__links-wrapper {
			display: flex;
			align-items: center;
			gap: var(--mdc-breadcrumb-gap);
		}

		/* Style slotted links */
		::slotted(mdc-link) {
			font-size: var(--mdc-breadcrumb-font-size);
			line-height: var(--mdc-breadcrumb-line-height);
		}

		/* Dividers between links - added as separate elements */
		.mdc-breadcrumb__divider {
			display: inline-flex;
			align-items: center;
			height: var(--mdc-breadcrumb-divider-height);
		}

		/* breakpoints.md */
		@media (min-width: 768px) {
			::slotted(mdc-link) {
				font-size: var(--mdc-breadcrumb-font-size-lg);
			}
			.mdc-breadcrumb__item--current,
			.mdc-breadcrumb__item--link a,
			.mdc-breadcrumb__name {
				font-size: var(--mdc-breadcrumb-font-size-lg);
			}

			.mdc-breadcrumb__item--separator {
				height: var(--mdc-breadcrumb-divider-height-lg);
			}

			.mdc-breadcrumb__avatar {
				width: var(--mdc-breadcrumb-avatar-size-lg);
				height: var(--mdc-breadcrumb-avatar-size-lg);
			}
		}
	`

  firstUpdated() {
    // After first render, insert dividers between links
    this.insertDividers()
  }

  private insertDividers() {
    const slot = this.shadowRoot?.querySelector('slot') as HTMLSlotElement
    if (!slot) return

    const links = slot.assignedElements()
    const wrapper = this._linkWrapperRef.value

    if (!wrapper || links.length === 0) return

    // Clear any existing dividers
    for (const el of wrapper.querySelectorAll('.mdc-breadcrumb__divider')) {
      el.remove()
    }

    // Insert dividers between each link (but not after the last link)
    links.forEach((link, index) => {
      // Move link to wrapper if not already there
      if (link.parentElement !== wrapper) {
        wrapper.appendChild(link)
      }

      // Add divider after each link except the last
      // The template handles the divider before the current item
      if (index < links.length - 1) {
        const divider = document.createElement('div')
        divider.className = 'mdc-breadcrumb__divider'
        const dividerEl = document.createElement('mdc-divider')
        dividerEl.setAttribute('orientation', 'vertical')
        divider.appendChild(dividerEl)

        // Insert after the current link
        if (link.nextSibling) {
          wrapper.insertBefore(divider, link.nextSibling)
        } else {
          wrapper.appendChild(divider)
        }
      }
    })
  }

  private handleSlotChange() {
    this.insertDividers()
  }

  render() {
    const className = `mdc-breadcrumb mdc-breadcrumb--type-${this.type}`

    return html`
			<nav aria-label="breadcrumb" class=${className}>
				${
          this.type === 'with-avatar'
            ? html`
							<img
								src=${this.avatarSrc}
								alt=${this.avatarAlt}
								class="mdc-breadcrumb__avatar"
							/>
							<div class="mdc-breadcrumb__name">${this.fullName}</div>
						`
            : ''
        }
				<ul>
					${
            this.type === 'with-avatar'
              ? html`
								<li class="mdc-breadcrumb__item mdc-breadcrumb__item--separator">
									<mdc-divider orientation="vertical"></mdc-divider>
								</li>
							`
              : ''
          }

					<li class="mdc-breadcrumb__item mdc-breadcrumb__links-wrapper">
						<div class="mdc-breadcrumb__links-wrapper" ${ref(this._linkWrapperRef)}>
							<slot @slotchange=${this.handleSlotChange}></slot>
						</div>
					</li>

					${
            this.current
              ? html`
								<li class="mdc-breadcrumb__item mdc-breadcrumb__item--separator">
									<mdc-divider orientation="vertical"></mdc-divider>
								</li>
								<li class="mdc-breadcrumb__item mdc-breadcrumb__item--current">
									<span>${this.current}</span>
								</li>
							`
              : ''
          }
				</ul>
			</nav>
		`
  }
}
